import { autoBeforeEachTest } from '@jira/platform__utils-auto-before-each-test/src/auto-before-each-test.tsx';
import { atlIndexedDb } from '@atlassian/browser-storage-controls/indexed-db-wrapper-service';
import { getProjectIdFromSpaState } from './getProjectIdFromSpaState.tsx';
import { getEarlyScriptObservabilityCache } from './getEarlyScriptObservabilityCache.tsx';
import {
	DB_NAME,
	DB_VERSION,
	INDEXEDDB_TIMEOUT_MS,
	STORE_NAME,
	CACHE_TTL_MS,
} from './dbName.constants.tsx';

interface ProjectIdMapping {
	[projectKey: string]: number;
}

interface ProjectMappingRecord {
	projectKey: string;
	projectId: number;
}

interface ProjectIdCacheEntry {
	promise: Promise<number | null>;
	expiry: number;
}

// Singleton pattern for database initialization
let dbInstance: Promise<IDBDatabase> | null = null;

// Cache for individual project ID lookups
const projectIdCache: Map<string, ProjectIdCacheEntry> = new Map();
autoBeforeEachTest(() => {
	projectIdCache.clear();
});

/**
 * Initialize IndexedDB database (singleton pattern)
 * @internal - Exported for testing purposes
 */
export const initDB = (): Promise<IDBDatabase> => {
	// eslint-disable-next-line jira/web-storage/no-direct-web-storage-usage -- early-fetch-cache runs pre-bootstrap and needs raw IndexedDB; pre-existing usage frozen for ratcheting
	if (!globalThis.indexedDB) {
		throw new Error('IndexedDB not supported');
	}

	if (dbInstance) {
		return dbInstance;
	}

	dbInstance = new Promise((resolve, reject) => {
		const request = atlIndexedDb.open(DB_NAME, DB_VERSION);

		request.onerror = () => {
			// Reset the singleton on error so we can retry
			dbInstance = null;
			reject(request.error);
		};

		// When routed through the Atlassian browser-storage-controls wrapper, a
		// consent-blocked open dispatches a `storageItemBlocked` event instead of
		// `error`, so it would otherwise never settle. `request.error` is null
		// here (this isn't a native IDB error), so reject with an explicit reason
		// instead so callers don't hang and get a meaningful cause.
		request.addEventListener('storageItemBlocked', () => {
			// Reset the singleton on error so we can retry
			dbInstance = null;
			reject(new Error('IndexedDB open blocked by consent settings'));
		});

		request.onsuccess = () => resolve(request.result);

		request.onupgradeneeded = (_event: IDBVersionChangeEvent) => {
			const db = request.result;
			if (!db.objectStoreNames.contains(STORE_NAME)) {
				db.createObjectStore(STORE_NAME, { keyPath: 'projectKey' });
			}
		};
	});

	return dbInstance;
};

/**
 * Get project ID mapping from IndexedDB
 */
const getProjectIdMappingFromStorage = async (
	operation: 'read' | 'write',
): Promise<ProjectIdMapping> => {
	try {
		const db = await initDB();
		const transaction = db.transaction([STORE_NAME], 'readonly');
		const store = transaction.objectStore(STORE_NAME);

		const result = await Promise.race([
			new Promise<ProjectIdMapping>((resolve) => {
				const start = performance.now();
				const request = store.getAll();
				request.onsuccess = () => {
					const records = request.result;
					const mapping: ProjectIdMapping = {};
					if (Array.isArray(records)) {
						records.forEach((record: ProjectMappingRecord) => {
							mapping[record.projectKey] = record.projectId;
						});
					}
					resolve(mapping);
					getEarlyScriptObservabilityCache()?.recordMetric(
						'custom',
						`get-project-id-mapping-from-indexdb-success-on-${operation}`,
						[{ name: 'jpdApp' }, { name: 'earlyScript' }],
						start,
						performance.now(),
					);
				};
				request.onerror = () => {
					getEarlyScriptObservabilityCache()?.recordError(
						'polaris.early-fetch-cache.project-id-mapping.indexdb',
						'IndexedDB getAll error',
					);
					resolve({});
				};
			}),
			new Promise<ProjectIdMapping>((_, reject) =>
				setTimeout(() => reject(new Error('IndexedDB read timed out')), INDEXEDDB_TIMEOUT_MS),
			),
		]);
		return result;
	} catch (error: unknown) {
		getEarlyScriptObservabilityCache()?.recordError(
			'polaris.early-fetch-cache.project-id-mapping.indexdb',
			'Error getting projectId from IndexedDB',
			error instanceof Error ? error : new Error(String(error)),
		);
		// do nothing
		return {};
	}
};

/**
 * Set project ID mapping in IndexedDB
 */
const setProjectIdMappingToStorage = async (mapping: ProjectIdMapping): Promise<void> => {
	try {
		const start = performance.now();
		const db = await initDB();
		const transaction = db.transaction([STORE_NAME], 'readwrite');
		const store = transaction.objectStore(STORE_NAME);

		// Clear existing data
		await new Promise<void>((resolve, reject) => {
			const clearRequest = store.clear();
			clearRequest.onsuccess = () => resolve();
			clearRequest.onerror = () => {
				getEarlyScriptObservabilityCache()?.recordError(
					'polaris.early-fetch-cache.project-id-mapping.indexdb',
					'IndexedDB clear error',
				);
				reject(clearRequest.error);
			};
		});

		// Add new data
		const promises = Object.entries(mapping).map(([projectKey, projectId]) => {
			return new Promise<void>((resolve, reject) => {
				const addRequest = store.add({ projectKey, projectId });
				addRequest.onsuccess = () => resolve();
				addRequest.onerror = () => {
					getEarlyScriptObservabilityCache()?.recordError(
						'polaris.early-fetch-cache.project-id-mapping.indexdb',
						'IndexedDB add error',
					);
					reject(addRequest.error);
				};
			});
		});

		await Promise.all(promises);
		getEarlyScriptObservabilityCache()?.recordMetric(
			'custom',
			'set-project-id-mapping-to-indexdb',
			[{ name: 'jpdApp' }, { name: 'earlyScript' }],
			start,
			performance.now(),
		);
	} catch (error: unknown) {
		getEarlyScriptObservabilityCache()?.recordError(
			'polaris.early-fetch-cache.project-id-mapping.indexdb',
			'Error setting projectId in IndexedDB',
			error instanceof Error ? error : new Error(String(error)),
		);
	}
};

/**
 * Get projectId from mapping if available
 */
export const getProjectIdFromMapping = async (projectKey: string): Promise<number | null> => {
	const start = performance.now();
	const mapping = await getProjectIdMappingFromStorage('read');
	getEarlyScriptObservabilityCache()?.recordMetric(
		'custom',
		'get-project-id-from-map',
		[{ name: 'jpdApp' }, { name: 'earlyScript' }],
		start,
		performance.now(),
	);
	return mapping[projectKey] || null;
};

/**
 * Set projectId in mapping for future use
 */
export const setProjectIdMapping = async (projectKey: string, projectId: number): Promise<void> => {
	const start = performance.now();

	const mapping = await getProjectIdMappingFromStorage('write');
	// Check if the mapping already exists with the same value
	if (mapping?.[projectKey] === projectId) {
		// Mapping already exists with the correct value, no need to update
		return;
	}

	mapping[projectKey] = projectId;

	await setProjectIdMappingToStorage(mapping);
	getEarlyScriptObservabilityCache()?.recordMetric(
		'custom',
		'set-project-id-mapping',
		[{ name: 'jpdApp' }, { name: 'earlyScript' }],
		start,
		performance.now(),
	);
};

/**
 * Get projectId with mapping - checks mapping first, then falls back to SPA_STATE
 */
export const getProjectIdFromMap = async (
	projectKey: string,
	waitForGlobalVariable: (variable: string) => Promise<void>,
): Promise<number | null> => {
	// Check cache first
	const cached = projectIdCache.get(projectKey);
	if (cached && Date.now() < cached.expiry) {
		return cached.promise;
	}

	const createPromise = async (): Promise<number | null> => {
		// First, try to get from mapping
		const mappedProjectId = await getProjectIdFromMapping(projectKey);

		if (mappedProjectId !== null) {
			return mappedProjectId;
		}

		// If not in mapping, wait for SPA_STATE and extract projectId
		try {
			await waitForGlobalVariable('SPA_STATE');
			const projectId = getProjectIdFromSpaState(projectKey);

			if (projectId) {
				return projectId;
			}
		} catch (error: unknown) {
			getEarlyScriptObservabilityCache()?.recordError(
				'polaris.early-fetch-cache.wait-for-global-variable',
				'Error getting projectId from SPA_STATE',
				error instanceof Error ? error : new Error(String(error)),
			);
		}

		return null;
	};

	const promise = createPromise();

	// Cache the promise
	projectIdCache.set(projectKey, {
		promise,
		expiry: Date.now() + CACHE_TTL_MS,
	});

	return promise;
};
